import React, { useState, useMemo } from 'react';
import { Card, Badge, Button, Modal, Collapse, Form, Row, Col, Pagination } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faMap, 
  faMapMarkerAlt, 
  faClock, 
  // faTachometerAlt, 
  // faRoad,
  faChevronDown,
  faChevronUp,
  faExclamationTriangle,
  faCheckCircle,
  faFilter,
  faEye,
  faEyeSlash
} from '@fortawesome/free-solid-svg-icons';
import { formatearFecha } from '../utils/procesadorEventos';
import MapaEventos from './MapaEventos';

export default function TablaDetalle({ vehiculo, datos, vehiculosMultiples = null, datosMultiples = null }) {
  const [showMapModal, setShowMapModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedVehiculo, setSelectedVehiculo] = useState(null); // Estado para vehículo específico en modo múltiple
  const [mapMode, setMapMode] = useState('general'); // 'general', 'individual', o 'vehiculo'
  const [showPolyline, setShowPolyline] = useState(false); // Estado para mostrar polilinea
  const [showNumbers, setShowNumbers] = useState(false); // Estado para mostrar numeración
  const [isTableCollapsed, setIsTableCollapsed] = useState(false);
  const [filtroProblema, setFiltroProblema] = useState('todos');
  const [vehiculosColapsados, setVehiculosColapsados] = useState({}); // Estado para colapsar vehículos individuales
  const [paginaActual, setPaginaActual] = useState(1); // Paginación para modo simple
  const [paginasVehiculos, setPaginasVehiculos] = useState({}); // Paginación para cada vehículo en modo múltiple
  const [registrosPorPagina, setRegistrosPorPagina] = useState(10); // Registros por página configurables
  const [busquedaTexto, setBusquedaTexto] = useState(''); // Búsqueda de texto en tabla
  const [filtroGSM, setFiltroGSM] = useState('todos'); // Filtro para módulo GSM (2G/LTE) - oculta vehículos completos

  const REGISTROS_POR_PAGINA = registrosPorPagina;

  // Detectar si estamos en modo múltiple
  const esModoMultiple = vehiculosMultiples && datosMultiples && vehiculosMultiples.length > 1;

  // Función para alternar el colapso de un vehículo específico
  const toggleVehiculoCollapse = (uidVehiculo) => {
    setVehiculosColapsados(prev => ({
      ...prev,
      [uidVehiculo]: !prev[uidVehiculo]
    }));
  };

  // Detectar problemas en secuencia de eventos
  const eventosProcesados = useMemo(() => {
    if (!datos || datos.length === 0) return [];
    
    const eventosOrdenados = datos.sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime());
    
    return eventosOrdenados.map((evento, index) => {
      const eventoAnterior = index > 0 ? eventosOrdenados[index - 1] : null;
      let tieneProblema = false;
      let tipoProblema = '';

      // Detectar dobles encendidos o apagados consecutivos
      if (eventoAnterior && eventoAnterior.tipoEvento === evento.tipoEvento) {
        tieneProblema = true;
        tipoProblema = `Doble ${evento.tipoEvento}`;
      }

      return {
        ...evento,
        tieneProblema,
        tipoProblema,
        index
      };
    });
  }, [datos]);

  // Filtrar eventos según el filtro seleccionado y búsqueda de texto
  const eventosFiltrados = useMemo(() => {
    let eventosFiltradosPorTipo = eventosProcesados;
    
    // Filtrar por tipo de problema
    switch (filtroProblema) {
      case 'problemas':
        eventosFiltradosPorTipo = eventosProcesados.filter(evento => evento.tieneProblema);
        break;
      case 'sinProblemas':
        eventosFiltradosPorTipo = eventosProcesados.filter(evento => !evento.tieneProblema);
        break;
      case 'todos':
      default:
        eventosFiltradosPorTipo = eventosProcesados;
        break;
    }
    
    // Filtrar por módulo GSM
    if (filtroGSM !== 'todos') {
      eventosFiltradosPorTipo = eventosFiltradosPorTipo.filter(evento => {
        const moduloGSM = evento.dispositivo?.moduloGSM?.toLowerCase();
        return moduloGSM === filtroGSM.toLowerCase();
      });
    }
    
    // Filtrar por texto de búsqueda
    if (busquedaTexto.trim() !== '') {
      const textoBusqueda = busquedaTexto.toLowerCase().trim();
      eventosFiltradosPorTipo = eventosFiltradosPorTipo.filter(evento =>
        evento.nombreEvento.toLowerCase().includes(textoBusqueda) ||
        formatearFecha(evento.fecha).toLowerCase().includes(textoBusqueda) ||
        (evento.tipoProblema && evento.tipoProblema.toLowerCase().includes(textoBusqueda))
      );
    }
    
    // Resetear paginación cuando cambian los filtros
    setPaginaActual(1);
    setPaginasVehiculos({});
    
    return eventosFiltradosPorTipo;
  }, [eventosProcesados, filtroProblema, filtroGSM, busquedaTexto]);

  // Paginación para modo simple
  const totalPaginasSimple = Math.ceil(eventosFiltrados.length / REGISTROS_POR_PAGINA);
  const indiceInicioSimple = (paginaActual - 1) * REGISTROS_POR_PAGINA;
  const indiceFinSimple = indiceInicioSimple + REGISTROS_POR_PAGINA;
  const eventosPaginadosSimple = eventosFiltrados.slice(indiceInicioSimple, indiceFinSimple);

  if (!vehiculo && !esModoMultiple) {
    return (
      <Card className="mt-3">
        <Card.Header>
          <h6 className="mb-0">Detalle de Eventos</h6>
        </Card.Header>
        <Card.Body>
          <div className="text-center py-4">
            <div className="mb-3">
              <FontAwesomeIcon icon={faMapMarkerAlt} size="2x" className="text-muted mb-2" />
              <h6 className="text-muted">No hay vehículos seleccionados</h6>
            </div>
            <p className="text-muted mb-2">
              <strong>Selección simple:</strong> Haz click en un vehículo para ver sus eventos
            </p>
            <p className="text-muted mb-0">
              <strong>Selección múltiple:</strong> Usa los checkboxes ☑️ para seleccionar varios vehículos
            </p>
            <small className="text-muted mt-2 d-block">
              💡 <strong>Tip:</strong> También puedes usar <kbd>Ctrl+Click</kbd> o <kbd>Shift+Click</kbd> para selección avanzada
            </small>
          </div>
        </Card.Body>
      </Card>
    );
  }

  const handleShowMap = (evento = null, vehiculoParam = null) => {
    // Debug: verificar qué evento se está pasando
    if (evento) {
      console.log('🗺️ Abriendo mapa para evento específico:', evento.nombreEvento, evento.fecha);
      setMapMode('individual'); // Modo individual: solo este evento
      setSelectedEvent(evento);
      setSelectedVehiculo(null);
    } else if (vehiculoParam && esModoMultiple) {
      console.log('🗺️ Abriendo mapa para vehículo específico:', vehiculoParam.alias);
      setMapMode('vehiculo'); // Modo vehículo específico: solo eventos de este vehículo
      setSelectedEvent(null);
      setSelectedVehiculo(vehiculoParam);
    } else {
      console.log('🗺️ Abriendo mapa general - todos los eventos');
      setMapMode('general'); // Modo general: todos los eventos
      setSelectedEvent(null);
      setSelectedVehiculo(null);
    }
    
    setShowMapModal(true);
  };

  // Preparar datos del mapa según el modo
  const prepararDatosMapa = () => {
    // Modo individual: solo mostrar el evento seleccionado
    if (mapMode === 'individual' && selectedEvent) {
      return {
        eventos: [selectedEvent], // Solo el evento seleccionado
        vehiculo: esModoMultiple ? null : vehiculo,
        titulo: `${selectedEvent.nombreEvento} - ${formatearFecha(selectedEvent.fecha)}`
      };
    }
    
    // Modo vehículo específico: solo eventos de un vehículo particular (en modo múltiple)
    if (mapMode === 'vehiculo' && selectedVehiculo && esModoMultiple) {
      const eventosVehiculo = datosMultiples[selectedVehiculo.uid_vehiculo] || [];
      
      // Procesar eventos para detectar problemas de este vehículo específico
      const eventosOrdenados = eventosVehiculo.sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime());
      const eventosProcesados = eventosOrdenados.map((evento, index) => {
        const eventoAnterior = index > 0 ? eventosOrdenados[index - 1] : null;
        let tieneProblema = false;
        let tipoProblema = '';

        // Detectar dobles encendidos o apagados consecutivos
        if (eventoAnterior && eventoAnterior.tipoEvento === evento.tipoEvento) {
          tieneProblema = true;
          tipoProblema = `Doble ${evento.tipoEvento}`;
        }

        return {
          ...evento,
          tieneProblema,
          tipoProblema,
          vehiculoInfo: selectedVehiculo,
          alias: selectedVehiculo.alias
        };
      });
      
      return {
        eventos: eventosProcesados,
        vehiculo: null, // En modo múltiple no pasamos vehículo individual
        titulo: `${selectedVehiculo.alias} - ${eventosProcesados.length} eventos`
      };
    }
    
    // Modo general: mostrar todos los eventos CON PROCESAMIENTO PARA DETECTAR PROBLEMAS
    if (esModoMultiple) {
      // Modo múltiple: combinar todos los eventos de todos los vehículos
      const todosEventos = [];
      vehiculosMultiples.forEach(vehiculoItem => {
        const eventosVehiculo = datosMultiples[vehiculoItem.uid_vehiculo] || [];
        
        // IMPORTANTE: Procesar eventos para detectar problemas por vehículo
        const eventosOrdenados = eventosVehiculo.sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime());
        const eventosProcesados = eventosOrdenados.map((evento, index) => {
          const eventoAnterior = index > 0 ? eventosOrdenados[index - 1] : null;
          let tieneProblema = false;
          let tipoProblema = '';

          // Detectar dobles encendidos o apagados consecutivos
          if (eventoAnterior && eventoAnterior.tipoEvento === evento.tipoEvento) {
            tieneProblema = true;
            tipoProblema = `Doble ${evento.tipoEvento}`;
          }

          return {
            ...evento,
            tieneProblema,
            tipoProblema,
            vehiculoInfo: vehiculoItem,
            alias: vehiculoItem.alias
          };
        });
        
        todosEventos.push(...eventosProcesados);
      });
      
      return {
        eventos: todosEventos,
        vehiculo: null,
        titulo: `${vehiculosMultiples.length} vehículos seleccionados`
      };
    } else {
      // Modo simple - usar eventos ya procesados
      return {
        eventos: eventosProcesados, // Ya tienen tieneProblema calculado
        vehiculo: vehiculo,
        titulo: vehiculo?.alias || 'Vehículo'
      };
    }
  };

  const handleCloseMap = () => {
    setShowMapModal(false);
    setSelectedEvent(null);
    setSelectedVehiculo(null); // Resetear vehículo seleccionado
    setMapMode('general'); // Resetear al modo general
  };

  const toggleTableCollapse = () => {
    setIsTableCollapsed(!isTableCollapsed);
  };

  // Función para renderizar la tabla de un vehículo específico
  const renderVehiculoTable = (vehiculoData, eventosData, isCollapsed = false, onToggleCollapse = null) => {
    if (!vehiculoData || !eventosData || eventosData.length === 0) return null;

    // Procesar eventos para este vehículo específico
    const eventosOrdenados = eventosData.sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime());
    
    const eventosProcesadosVehiculo = eventosOrdenados.map((evento, index) => {
      const eventoAnterior = index > 0 ? eventosOrdenados[index - 1] : null;
      let tieneProblema = false;
      let tipoProblema = '';

      // Detectar dobles encendidos o apagados consecutivos
      if (eventoAnterior && eventoAnterior.tipoEvento === evento.tipoEvento) {
        tieneProblema = true;
        tipoProblema = `Doble ${evento.tipoEvento}`;
      }

      return {
        ...evento,
        tieneProblema,
        tipoProblema,
        index
      };
    });

    // Filtrar eventos según el filtro seleccionado y búsqueda
    const eventosFiltradosVehiculo = eventosProcesadosVehiculo.filter(evento => {
      // Filtro por tipo de problema
      let pasaFiltroTipo = true;
      switch (filtroProblema) {
        case 'problemas':
          pasaFiltroTipo = evento.tieneProblema;
          break;
        case 'sinProblemas':
          pasaFiltroTipo = !evento.tieneProblema;
          break;
        case 'todos':
        default:
          pasaFiltroTipo = true;
          break;
      }
      
      // Filtro por texto de búsqueda
      let pasaFiltroBusqueda = true;
      if (busquedaTexto.trim() !== '') {
        const textoBusqueda = busquedaTexto.toLowerCase().trim();
        pasaFiltroBusqueda = 
          evento.nombreEvento.toLowerCase().includes(textoBusqueda) ||
          formatearFecha(evento.fecha).toLowerCase().includes(textoBusqueda) ||
          (evento.tipoProblema && evento.tipoProblema.toLowerCase().includes(textoBusqueda));
      }
      
      // Filtro por módulo GSM
      let pasaFiltroGSM = true;
      if (filtroGSM !== 'todos') {
        const moduloGSM = evento.dispositivo?.moduloGSM?.toLowerCase();
        pasaFiltroGSM = moduloGSM === filtroGSM.toLowerCase();
      }
      
      return pasaFiltroTipo && pasaFiltroBusqueda && pasaFiltroGSM;
    });

    // Paginación para este vehículo
    const paginaVehiculo = paginasVehiculos[vehiculoData.uid_vehiculo] || 1;
    const totalPaginas = Math.ceil(eventosFiltradosVehiculo.length / REGISTROS_POR_PAGINA);
    const indiceInicio = (paginaVehiculo - 1) * REGISTROS_POR_PAGINA;
    const indiceFin = indiceInicio + REGISTROS_POR_PAGINA;
    const eventosPaginados = eventosFiltradosVehiculo.slice(indiceInicio, indiceFin);

    const totalEventosConProblemasVehiculo = eventosProcesadosVehiculo.filter(e => e.tieneProblema).length;

    const handleCambiarPagina = (nuevaPagina) => {
      setPaginasVehiculos(prev => ({
        ...prev,
        [vehiculoData.uid_vehiculo]: nuevaPagina
      }));
    };

    return (
      <Card className="mt-2" key={vehiculoData.uid_vehiculo || 'single'}>
        <Card.Header 
          className="d-flex justify-content-between align-items-center cursor-pointer" 
          onClick={onToggleCollapse}
          style={{ cursor: 'pointer' }}
        >
          <div className="d-flex align-items-center">
            <Button 
              variant={isCollapsed ? "outline-secondary" : "secondary"}
              size="sm"
              className="me-3"
              onClick={(e) => {
                e.stopPropagation();
                onToggleCollapse();
              }}
            >
              <FontAwesomeIcon 
                icon={isCollapsed ? faChevronDown : faChevronUp} 
              />
            </Button>
            <h6 className="mb-0">
              <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2 text-primary" />
              {vehiculoData.alias} - {vehiculoData.totalApagados} Apagados | {vehiculoData.totalEncendidos} Encendidos
              {totalEventosConProblemasVehiculo > 0 && (
                <Badge bg="danger" className="ms-2">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                  {totalEventosConProblemasVehiculo} problemas
                </Badge>
              )}
            </h6>
          </div>
          <Button 
            variant="outline-primary" 
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleShowMap(null, vehiculoData);
            }}
          >
            <FontAwesomeIcon icon={faMap} className="me-1" />
            Ver Mapa
          </Button>
        </Card.Header>

        <Collapse in={!isCollapsed}>
          <div>
            <Card.Body className="p-0">
              <div style={{ 
                maxHeight: `${8 * 45 + 45}px`, // 8 filas * 45px por fila + header = ~405px
                overflowY: 'auto',
                overflowX: 'hidden'
              }}>
                <table className='fs-10 mb-0 overflow-hidden table table-striped'>
                  <thead className="table-light position-sticky" style={{ top: 0, zIndex: 10 }}>
                    <tr>
                      <th className="text-900" style={{width: '5%'}}></th>
                      <th className="text-900" style={{width: '20%'}}>Fecha y Hora</th>
                      <th className="text-900" style={{width: '15%'}}>Evento</th>
                      {/* <th className="text-900" style={{width: '10%'}}>Velocidad</th>
                      <th className="text-900" style={{width: '10%'}}>Distancia</th> */}
                      <th className="text-900" style={{width: '15%'}}>Hardware</th>
                      <th className="text-900" style={{width: '15%'}}>Firmware</th>
                      <th className="text-900" style={{width: '10%'}}>GSM</th>
                      <th className="text-900 text-center" style={{width: '20%'}}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eventosPaginados.map((evento, index) => (
                      <tr 
                        key={index}
                        className={evento.tieneProblema ? 'table-warning' : ''}
                        style={evento.tieneProblema ? { borderLeft: '4px solid #dc3545' } : {}}
                      >
                        <td className="text-center">
                          {evento.tieneProblema ? (
                            <FontAwesomeIcon 
                              icon={faExclamationTriangle} 
                              className="text-danger" 
                              title={evento.tipoProblema}
                            />
                          ) : (
                            <FontAwesomeIcon 
                              icon={faCheckCircle} 
                              className="text-success" 
                              title="Sin problemas"
                            />
                          )}
                        </td>
                        <td>
                          <div className="d-flex align-items-center">
                            <FontAwesomeIcon icon={faClock} className="me-2 text-muted" />
                            <span className="label-detail-card">
                              {formatearFecha(evento.fecha)}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className="d-flex flex-column">
                            <Badge 
                              pill 
                              bg={evento.tipoEvento === 'encendido' ? 'success' : 'danger'}
                              text="white"
                              className="fw-semibold"
                              style={{ width: 'fit-content', minWidth: '90px', fontSize: '0.75rem' }}
                            >
                              {evento.nombreEvento}
                            </Badge>
                            {evento.tieneProblema && (
                              <small className="text-danger mt-1 fw-bold">
                                {evento.tipoProblema}
                              </small>
                            )}
                          </div>
                        </td>
                        {/* <td>
                          <div className="d-flex align-items-center">
                            <FontAwesomeIcon icon={faTachometerAlt} className="me-2 text-muted" />
                            <span className="label-detail-card">
                              {formatearVelocidad(evento.velocidad)}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className="d-flex align-items-center">
                            <FontAwesomeIcon icon={faRoad} className="me-2 text-muted" />
                            <span className="label-detail-card">
                              {formatearDistancia(evento.distancia)}
                            </span>
                          </div>
                        </td> */}
                        <td>
                          <span className="label-detail-card text-muted">
                            {evento.dispositivo?.versionHardware || 'N/A'}
                          </span>
                        </td>
                        <td>
                          <span className="label-detail-card text-muted">
                            {evento.dispositivo?.versionFirmware || 'N/A'}
                          </span>
                        </td>
                        <td>
                          <Badge 
                            pill 
                            bg={evento.dispositivo?.moduloGSM === 'LTE' ? 'primary' : 'secondary'}
                            text="white"
                            className="fw-semibold"
                            style={{ fontSize: '0.7rem', minWidth: '30px' }}
                          >
                            {evento.dispositivo?.moduloGSM || 'N/A'}
                          </Badge>
                        </td>
                        <td className="text-center">
                          <Button 
                            variant="outline-secondary" 
                            size="sm"
                            className="label-detail-card"
                            onClick={() => handleShowMap(evento, vehiculoData)}
                          >
                            <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1" />
                            Ver
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Paginación en el pie de la tabla */}
              {totalPaginas > 1 && (
                <div className="border-top">
                  <div className="d-flex justify-content-between align-items-center px-3 py-2 bg-light">
                    <small className="text-muted fs-11">
                      Mostrando {indiceInicio + 1}-{Math.min(indiceFin, eventosFiltradosVehiculo.length)} de {eventosFiltradosVehiculo.length} eventos
                    </small>
                    <Pagination size="sm" className="mb-0">
                      <Pagination.Prev 
                        disabled={paginaVehiculo === 1}
                        onClick={() => handleCambiarPagina(paginaVehiculo - 1)}
                      />
                      {[...Array(totalPaginas)].map((_, index) => (
                        <Pagination.Item
                          key={index + 1}
                          active={index + 1 === paginaVehiculo}
                          onClick={() => handleCambiarPagina(index + 1)}
                        >
                          {index + 1}
                        </Pagination.Item>
                      ))}
                      <Pagination.Next 
                        disabled={paginaVehiculo === totalPaginas}
                        onClick={() => handleCambiarPagina(paginaVehiculo + 1)}
                      />
                    </Pagination>
                  </div>
                </div>
              )}
              
              {eventosFiltradosVehiculo.length === 0 && (
                <div className="text-center py-4 text-muted">
                  <p className="mb-0 fs-10 fw-semibold">
                    No hay eventos que coincidan con el filtro seleccionado
                  </p>
                </div>
              )}
            </Card.Body>
          </div>
        </Collapse>
      </Card>
    );
  };

  return (
    <>
      {/* Modo múltiple: vista simplificada */}
      {esModoMultiple ? (
        <div className="mt-3">
          {/* Barra de filtros globales con layout de 2 filas */}
          <Card className="mb-3 shadow-sm">
            <Card.Body className="py-2">
              <Row className="align-items-end g-2">
                {/* Botón colapsar - Sin etiqueta */}
                <Col md={1}>
                  <div className="text-center">
                    <Button 
                      variant="outline-secondary" 
                      size="sm"
                      onClick={() => {
                        const todosColapsados = vehiculosMultiples.every(v => vehiculosColapsados[v.uid_vehiculo]);
                        const nuevoEstado = {};
                        vehiculosMultiples.forEach(v => {
                          nuevoEstado[v.uid_vehiculo] = !todosColapsados;
                        });
                        setVehiculosColapsados(nuevoEstado);
                      }}
                      className="d-flex align-items-center justify-content-center"
                      style={{ height: '32px', width: '100%' }}
                      title={vehiculosMultiples.every(v => vehiculosColapsados[v.uid_vehiculo]) ? 'Expandir todas las tablas' : 'Colapsar todas las tablas'}
                    >
                      <FontAwesomeIcon 
                        icon={vehiculosMultiples.every(v => vehiculosColapsados[v.uid_vehiculo]) ? faChevronDown : faChevronUp} 
                      />
                    </Button>
                  </div>
                </Col>
                
                {/* Contador - Sin etiqueta */}
                <Col md={1}>
                  <div className="text-center">
                    <div className="text-primary fw-bold fs-11" style={{ lineHeight: '1.2', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {(() => {
                        let vehiculosVisibles = 0;
                        vehiculosMultiples.forEach((vehiculoItem) => {
                          const eventosVehiculo = datosMultiples[vehiculoItem.uid_vehiculo] || [];
                          
                          // Verificar filtro GSM
                          if (filtroGSM !== 'todos') {
                            const tieneEventosConFiltroGSM = eventosVehiculo.some(evento => {
                              const moduloGSM = evento.dispositivo?.moduloGSM?.toLowerCase();
                              return moduloGSM === filtroGSM.toLowerCase();
                            });
                            if (!tieneEventosConFiltroGSM) return;
                          }
                          
                          // Verificar filtro de problemas
                          if (filtroProblema !== 'todos') {
                            const eventosOrdenados = eventosVehiculo.sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime());
                            const eventosProcesados = eventosOrdenados.map((evento, index) => {
                              const eventoAnterior = index > 0 ? eventosOrdenados[index - 1] : null;
                              let tieneProblema = false;
                              if (eventoAnterior && eventoAnterior.tipoEvento === evento.tipoEvento) {
                                tieneProblema = true;
                              }
                              return { ...evento, tieneProblema };
                            });
                            
                            const tieneProblemas = eventosProcesados.some(e => e.tieneProblema);
                            if (filtroProblema === 'problemas' && !tieneProblemas) return;
                            if (filtroProblema === 'sinProblemas' && tieneProblemas) return;
                          }
                          
                          vehiculosVisibles++;
                        });
                        return vehiculosVisibles;
                      })()}
                    </div>
                    <small className="text-muted d-block" style={{ fontSize: '9px', lineHeight: '1' }}>
                      unidades
                    </small>
                  </div>
                </Col>
                
                {/* Filtro Eventos - Con etiqueta encima */}
                <Col md={3}>
                  <div>
                    <small className="text-muted d-block mb-1 fs-11 fw-semibold">Eventos</small>
                    <div className="d-flex align-items-center">
                      <FontAwesomeIcon icon={faFilter} className="me-2 text-primary" />
                      <Form.Select 
                        size="sm" 
                        value={filtroProblema}
                        onChange={(e) => setFiltroProblema(e.target.value)}
                        className="fs-11 fw-semibold"
                        style={{ height: '32px' }}
                      >
                        <option value="todos">📊 Todos</option>
                        <option value="problemas">⚠️ Solo problemas</option>
                        <option value="sinProblemas">✅ Sin problemas</option>
                      </Form.Select>
                    </div>
                  </div>
                </Col>
                
                {/* Filtro GSM - Con etiqueta encima */}
                <Col md={2}>
                  <div>
                    <small className="text-muted d-block mb-1 fs-11 fw-semibold">GSM</small>
                    <Form.Select 
                      size="sm" 
                      value={filtroGSM}
                      onChange={(e) => setFiltroGSM(e.target.value)}
                      className="fs-11 fw-semibold"
                      style={{ height: '32px' }}
                    >
                      <option value="todos">Todos</option>
                      <option value="2G">2G</option>
                      <option value="LTE">LTE</option>
                    </Form.Select>
                  </div>
                </Col>
                
                {/* Búsqueda - Con etiqueta encima */}
                <Col md={3}>
                  <div>
                    <small className="text-muted d-block mb-1 fs-11 fw-semibold">Buscar</small>
                    <Form.Control
                      type="text"
                      size="sm"
                      placeholder="🔍 Texto..."
                      value={busquedaTexto}
                      onChange={(e) => setBusquedaTexto(e.target.value)}
                      className="fs-11"
                      style={{ height: '32px' }}
                    />
                  </div>
                </Col>
                
                {/* Combo paginación - Sin etiqueta */}
                <Col md={2}>
                  <div className="text-center">
                    <Form.Select 
                      size="sm" 
                      value={registrosPorPagina}
                      onChange={(e) => setRegistrosPorPagina(Number(e.target.value))}
                      className="fs-11 text-center"
                      style={{ height: '32px', fontSize: '10px' }}
                      title="Registros por página"
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={1000}>1K</option>
                    </Form.Select>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Renderizar cada vehículo por separado */}
          {vehiculosMultiples.map((vehiculoItem) => {
            const eventosVehiculo = datosMultiples[vehiculoItem.uid_vehiculo] || [];
            const isVehiculoCollapsed = vehiculosColapsados[vehiculoItem.uid_vehiculo] || false;
            
            // Verificar si el vehículo tiene eventos que coincidan con el filtro GSM
            if (filtroGSM !== 'todos') {
              const tieneEventosConFiltroGSM = eventosVehiculo.some(evento => {
                const moduloGSM = evento.dispositivo?.moduloGSM?.toLowerCase();
                return moduloGSM === filtroGSM.toLowerCase();
              });
              
              // Si no tiene eventos que coincidan con el filtro GSM, no renderizar el vehículo
              if (!tieneEventosConFiltroGSM) {
                return null;
              }
            }
            
            // Verificar si el vehículo tiene problemas para el filtro de problemas
            if (filtroProblema !== 'todos') {
              const eventosOrdenados = eventosVehiculo.sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime());
              const eventosProcesados = eventosOrdenados.map((evento, index) => {
                const eventoAnterior = index > 0 ? eventosOrdenados[index - 1] : null;
                let tieneProblema = false;
                
                // Detectar dobles encendidos o apagados consecutivos
                if (eventoAnterior && eventoAnterior.tipoEvento === evento.tipoEvento) {
                  tieneProblema = true;
                }
                
                return { ...evento, tieneProblema };
              });
              
              const tieneProblemas = eventosProcesados.some(evento => evento.tieneProblema);
              
              // Si filtro es "problemas" y no tiene problemas, ocultar vehículo
              if (filtroProblema === 'problemas' && !tieneProblemas) {
                return null;
              }
              
              // Si filtro es "sinProblemas" y tiene problemas, ocultar vehículo
              if (filtroProblema === 'sinProblemas' && tieneProblemas) {
                return null;
              }
            }
            
            return renderVehiculoTable(
              vehiculoItem,
              eventosVehiculo,
              isVehiculoCollapsed,
              () => toggleVehiculoCollapse(vehiculoItem.uid_vehiculo)
            );
          }).filter(Boolean)} {/* Filtrar elementos null */}
          
          {/* Mensaje cuando no hay vehículos visibles */}
          {vehiculosMultiples.map((vehiculoItem) => {
            const eventosVehiculo = datosMultiples[vehiculoItem.uid_vehiculo] || [];
            
            // Aplicar misma lógica de filtrado para contar vehículos visibles
            if (filtroGSM !== 'todos') {
              const tieneEventosConFiltroGSM = eventosVehiculo.some(evento => {
                const moduloGSM = evento.dispositivo?.moduloGSM?.toLowerCase();
                return moduloGSM === filtroGSM.toLowerCase();
              });
              if (!tieneEventosConFiltroGSM) return null;
            }
            
            if (filtroProblema !== 'todos') {
              const eventosOrdenados = eventosVehiculo.sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime());
              const eventosProcesados = eventosOrdenados.map((evento, index) => {
                const eventoAnterior = index > 0 ? eventosOrdenados[index - 1] : null;
                let tieneProblema = false;
                if (eventoAnterior && eventoAnterior.tipoEvento === evento.tipoEvento) {
                  tieneProblema = true;
                }
                return { ...evento, tieneProblema };
              });
              
              const tieneProblemas = eventosProcesados.some(evento => evento.tieneProblema);
              if (filtroProblema === 'problemas' && !tieneProblemas) return null;
              if (filtroProblema === 'sinProblemas' && tieneProblemas) return null;
            }
            
            return vehiculoItem;
          }).filter(Boolean).length === 0 && (
            <Card className="mt-3">
              <Card.Body>
                <div className="text-center py-4">
                  <FontAwesomeIcon icon={faFilter} size="2x" className="text-muted mb-3" />
                  <h6 className="text-muted mb-2">No hay vehículos que coincidan con los filtros</h6>
                  <p className="text-muted mb-0 fs-11">
                    Intenta cambiar los criterios de filtrado para ver más resultados
                  </p>
                </div>
              </Card.Body>
            </Card>
          )}
        </div>
      ) : (
        /* Modo simple: vehículo único (comportamiento original) */
        <Card className="mt-3">
          <Card.Header 
            className="d-flex justify-content-between align-items-center cursor-pointer"
            onClick={toggleTableCollapse}
            style={{ cursor: 'pointer' }}
          >
            <div className="d-flex align-items-center">
              <Button 
                variant={isTableCollapsed ? "outline-secondary" : "secondary"}
                size="sm"
                className="me-3"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleTableCollapse();
                }}
              >
                <FontAwesomeIcon 
                  icon={isTableCollapsed ? faChevronDown : faChevronUp} 
                />
              </Button>
              <h6 className="mb-0">
                <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2 text-primary" />
                {vehiculo?.alias || 'Detalle de Eventos'} - {vehiculo?.totalApagados || 0} Apagados | {vehiculo?.totalEncendidos || 0} Encendidos
                {eventosProcesados.filter(e => e.tieneProblema).length > 0 && (
                  <Badge bg="danger" className="ms-2">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                    {eventosProcesados.filter(e => e.tieneProblema).length} problemas
                  </Badge>
                )}
              </h6>
            </div>
            <Button 
              variant="outline-primary" 
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleShowMap();
              }}
            >
              <FontAwesomeIcon icon={faMap} className="me-1" />
              Ver Mapa
            </Button>
          </Card.Header>

          {!vehiculo || !datos || datos.length === 0 ? (
            <Card.Body>
              <p className="text-muted text-center py-3">
                Selecciona un vehículo para ver el detalle de sus eventos de apagado y encendido.
              </p>
            </Card.Body>
          ) : (
            <Collapse in={!isTableCollapsed}>
              <div>
                {/* Filtros */}
                <div className="px-3 py-2 bg-light border-bottom">
                  <Row className="align-items-center mb-2">
                    <Col md={3}>
                      <div className="d-flex align-items-center">
                        <FontAwesomeIcon icon={faFilter} className="me-2 text-muted" />
                        <Form.Select 
                          size="sm" 
                          value={filtroProblema}
                          onChange={(e) => setFiltroProblema(e.target.value)}
                          style={{ width: '200px' }}
                          className="fs-11"
                        >
                          <option value="todos">Todos los eventos ({eventosProcesados.length})</option>
                          <option value="problemas">Solo problemas ({eventosProcesados.filter(e => e.tieneProblema).length})</option>
                          <option value="sinProblemas">Sin problemas ({eventosProcesados.length - eventosProcesados.filter(e => e.tieneProblema).length})</option>
                        </Form.Select>
                      </div>
                    </Col>
                    <Col md={2}>
                      <div className="d-flex align-items-center">
                        <small className="text-muted fs-11 me-2">GSM:</small>
                        <Form.Select 
                          size="sm" 
                          value={filtroGSM}
                          onChange={(e) => setFiltroGSM(e.target.value)}
                          style={{ width: '80px' }}
                          className="fs-11"
                        >
                          <option value="todos">Todos</option>
                          <option value="2G">2G</option>
                          <option value="LTE">LTE</option>
                        </Form.Select>
                      </div>
                    </Col>
                    <Col md={4}>
                      <Form.Control
                        type="text"
                        size="sm"
                        placeholder="🔍 Buscar en eventos..."
                        value={busquedaTexto}
                        onChange={(e) => setBusquedaTexto(e.target.value)}
                        className="fs-11"
                      />
                    </Col>
                    <Col md={3}>
                      <div className="d-flex align-items-center">
                        <small className="text-muted fs-11 me-2">Mostrar:</small>
                        <Form.Select 
                          size="sm" 
                          value={registrosPorPagina}
                          onChange={(e) => setRegistrosPorPagina(Number(e.target.value))}
                          style={{ width: '80px' }}
                          className="fs-11"
                        >
                          <option value={10}>10</option>
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                          <option value={1000}>1000</option>
                        </Form.Select>
                      </div>
                    </Col>
                  </Row>
                </div>

                <Card.Body className="p-0">
                  <div style={{ 
                    maxHeight: `${8 * 45 + 45}px`, // 8 filas * 45px por fila + header = ~405px
                    overflowY: 'auto',
                    overflowX: 'hidden'
                  }}>
                    <table className='fs-10 mb-0 overflow-hidden table table-striped'>
                      <thead className="table-light position-sticky" style={{ top: 0, zIndex: 10 }}>
                        <tr>
                          <th className="text-900" style={{width: '5%'}}></th>
                          <th className="text-900" style={{width: '20%'}}>Fecha y Hora</th>
                          <th className="text-900" style={{width: '15%'}}>Evento</th>
                          {/* <th className="text-900" style={{width: '15%'}}>Velocidad</th>
                          <th className="text-900" style={{width: '15%'}}>Distancia</th> */}
                          <th className="text-900" style={{width: '15%'}}>Hardware</th>
                          <th className="text-900" style={{width: '15%'}}>Firmware</th>
                          <th className="text-900" style={{width: '10%'}}>GSM</th>
                          <th className="text-900 text-center" style={{width: '20%'}}>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {eventosPaginadosSimple.map((evento, index) => (
                          <tr 
                            key={index}
                            className={evento.tieneProblema ? 'table-warning' : ''}
                            style={evento.tieneProblema ? { borderLeft: '4px solid #dc3545' } : {}}
                          >
                            <td className="text-center">
                              {evento.tieneProblema ? (
                                <FontAwesomeIcon 
                                  icon={faExclamationTriangle} 
                                  className="text-danger" 
                                  title={evento.tipoProblema}
                                />
                              ) : (
                                <FontAwesomeIcon 
                                  icon={faCheckCircle} 
                                  className="text-success" 
                                  title="Sin problemas"
                                />
                              )}
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                <FontAwesomeIcon icon={faClock} className="me-2 text-muted" />
                                <span className="label-detail-card">
                                  {formatearFecha(evento.fecha)}
                                </span>
                              </div>
                            </td>
                            <td>
                              <div className="d-flex flex-column">
                                <Badge 
                                  pill 
                                  bg={evento.tipoEvento === 'encendido' ? 'success' : 'danger'}
                                  text="white"
                                  className="fw-semibold"
                                  style={{ width: 'fit-content', minWidth: '90px', fontSize: '0.75rem' }}
                                >
                                  {evento.nombreEvento}
                                </Badge>
                                {evento.tieneProblema && (
                                  <small className="text-danger mt-1 fw-bold">
                                    {evento.tipoProblema}
                                  </small>
                                )}
                                {evento.alias && vehiculo.alias.includes('vehículos seleccionados') && (
                                  <small className="text-muted mt-1" style={{ fontSize: '0.65rem' }}>
                                    {evento.alias || evento.no_economico}
                                  </small>
                                )}
                              </div>
                            </td>
                            {/* <td>
                              <div className="d-flex align-items-center">
                                <span className="label-detail-card">
                                  {formatearVelocidad(evento.velocidad)}
                                </span>
                              </div>
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                <span className="label-detail-card">
                                  {formatearDistancia(evento.distancia)}
                                </span>
                              </div>
                            </td> */}
                            <td>
                              <span className="label-detail-card text-muted">
                                {evento.dispositivo?.versionHardware || 'N/A'}
                              </span>
                            </td>
                            <td>
                              <span className="label-detail-card text-muted">
                                {evento.dispositivo?.versionFirmware || 'N/A'}
                              </span>
                            </td>
                            <td>
                              <Badge 
                                pill 
                                bg={evento.dispositivo?.moduloGSM === 'LTE' ? 'primary' : 'secondary'}
                                text="white"
                                className="fw-semibold"
                                style={{ fontSize: '0.7rem', minWidth: '30px' }}
                              >
                                {evento.dispositivo?.moduloGSM || 'N/A'}
                              </Badge>
                            </td>
                            <td className="text-center">
                              <Button 
                                variant="outline-secondary" 
                                size="sm"
                                className="label-detail-card"
                                onClick={() => handleShowMap(evento)}
                              >
                                <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1" />
                                Ver
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Paginación en el footer de la tabla */}
                  {totalPaginasSimple > 1 && (
                    <div className="border-top">
                      <div className="d-flex justify-content-between align-items-center px-3 py-2 bg-light">
                        <small className="text-muted fs-11">
                          Mostrando {indiceInicioSimple + 1}-{Math.min(indiceFinSimple, eventosFiltrados.length)} de {eventosFiltrados.length} eventos
                        </small>
                        <Pagination size="sm" className="mb-0">
                          <Pagination.Prev 
                            disabled={paginaActual === 1}
                            onClick={() => setPaginaActual(paginaActual - 1)}
                          />
                          {[...Array(totalPaginasSimple)].map((_, index) => (
                            <Pagination.Item
                              key={index + 1}
                              active={index + 1 === paginaActual}
                              onClick={() => setPaginaActual(index + 1)}
                            >
                              {index + 1}
                            </Pagination.Item>
                          ))}
                          <Pagination.Next 
                            disabled={paginaActual === totalPaginasSimple}
                            onClick={() => setPaginaActual(paginaActual + 1)}
                          />
                        </Pagination>
                      </div>
                    </div>
                  )}
                  
                  {eventosPaginadosSimple.length === 0 && eventosFiltrados.length === 0 && (
                    <div className="text-center py-4 text-muted">
                      <p className="mb-0 fs-10 fw-semibold">
                        No hay eventos que coincidan con el filtro seleccionado
                      </p>
                    </div>
                  )}
                </Card.Body>
              </div>
            </Collapse>
          )}
        </Card>
      )}

      {/* Modal del Mapa */}
      <Modal 
        show={showMapModal} 
        onHide={handleCloseMap} 
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faMap} className="me-2" />
            {mapMode === 'individual' && selectedEvent ? 
              `📍 ${selectedEvent.nombreEvento} - ${formatearFecha(selectedEvent.fecha)}` : 
              mapMode === 'vehiculo' && selectedVehiculo ?
                `🚗 ${selectedVehiculo.alias} - Eventos en Mapa` :
                esModoMultiple ? 
                  `🗺️ Eventos en Mapa - ${vehiculosMultiples.length} vehículos` :
                  `🗺️ Eventos en Mapa - ${vehiculo?.alias || 'Vehículo'}`
            }
          </Modal.Title>
        </Modal.Header>
        
        {/* Controles adicionales solo para modo general y vehículo específico */}
        {(mapMode === 'general' || mapMode === 'vehiculo') && (
          <div className="px-3 py-2 bg-light border-bottom">
            <Row className="align-items-center">
              <Col md={6}>
                <div className="d-flex align-items-center">
                  <Form.Check
                    type="checkbox"
                    id="showPolyline"
                    label="Unir puntos con polilinea"
                    checked={showPolyline}
                    onChange={(e) => setShowPolyline(e.target.checked)}
                    className="me-3 fs-11"
                  />
                  <Form.Check
                    type="checkbox"
                    id="showNumbers"
                    label="Numerar marcadores"
                    checked={showNumbers}
                    onChange={(e) => setShowNumbers(e.target.checked)}
                    className="fs-11"
                  />
                </div>
              </Col>
              <Col md={6} className="text-end">
                <small className="text-muted fs-11">
                  {showPolyline && showNumbers ? '🔗📍' : showPolyline ? '🔗' : showNumbers ? '📍' : '🗺️'} 
                  {' '}Opciones de visualización
                </small>
              </Col>
            </Row>
          </div>
        )}
        
        <Modal.Body className="p-0">
          <MapaEventos
            eventos={(() => {
              const datosMapa = prepararDatosMapa();
              return datosMapa.eventos;
            })()}
            selectedEvent={mapMode === 'individual' ? null : selectedEvent} // En modo individual no resaltar, solo mostrar ese evento
            vehiculo={(() => {
              const datosMapa = prepararDatosMapa();
              return datosMapa.vehiculo;
            })()}
            altura="500px"
            showPolyline={(mapMode === 'general' || mapMode === 'vehiculo') ? showPolyline : false} // Solo en modo general y vehículo
            showNumbers={(mapMode === 'general' || mapMode === 'vehiculo') ? showNumbers : false} // Solo en modo general y vehículo
          />
        </Modal.Body>
      </Modal>
    </>
  );
}
